import type { Lang } from '../../lib/i18n'
import type { OrderStatusValue } from '../../lib/orders-api'

// Screen-local dictionary — kept alongside the feature (not the shared src/locales/*.json)
// so this bench stays inside files_locked. Same shape/pattern as lib/i18n.ts.
const EN = {
  back: 'Back',
  loading: 'Loading order…',
  errorLoading: "Couldn't load this order.",
  retry: 'Retry',
  notFound: 'Order not found.',

  statusUpdated: 'Status updated',
  undo: 'Undo',
  undoingIn: (s: number) => `Undo (${s}s)`,
  advanceConfirmNone: 'No further forward step from this status.',

  actions: 'Actions',
  cancelOrder: 'Cancel order',
  cancelConfirmTitle: 'Cancel this order?',
  cancelConfirmBody: 'This cannot be undone. The customer will be notified.',
  cancelConfirmAction: 'Yes, cancel order',
  cancelConfirmDismiss: 'Keep order',
  amend: 'Amend',
  trackLink: 'Operator tracking link',

  channel: 'Channel',
  sourceSystem: 'Source',
  fulfillmentMethod: 'Fulfillment',
  orderType: 'Type',
  delivery: 'Delivery',
  pickup: 'Pickup',
  placedAt: 'Placed',

  customer: 'Customer',
  whatsapp: 'WhatsApp',
  language: 'Language',
  returning: 'Returning',
  newCustomer: 'New customer',
  ordersCount: 'orders',
  spent: 'spent',
  testOrder: 'TEST',
  whatsappMessage: (ref: string) =>
    `Hi! Following up on your order ${ref}. / مرحباً! بخصوص طلبك ${ref}.`,

  deliveryTitle: 'Delivery',
  street: 'Street',
  building: 'Building / floor',
  city: 'City',
  zone: 'Zone',
  deliveryFee: 'Delivery fee',
  originalFee: 'original',
  deliveryDate: 'Date',
  deliverySlot: 'Slot',
  deliveryNotes: 'Landmark / directions',
  giftMessage: 'Gift message',
  noDeliveryInfo: 'Pickup order — no delivery address.',

  itemsTitle: 'What to prepare',
  qty: 'Qty',
  each: 'each',
  customizations: 'Customizations',
  allergenFlag: 'Check allergens/substitutions',
  promoTag: 'Promo',
  noItems: 'No items on this order.',
  unmappedItem: 'Not on our menu: check this item with Toters',

  totalsTitle: 'Totals',
  subtotal: 'Subtotal',
  grandTotal: 'Grand total',
  cod: 'COD — collect on delivery',

  activityTitle: 'Activity',
  noActivity: 'No status changes yet.',
  changedBySystem: 'System',
  from: 'from',
  to: 'to',

  roleView: 'View only',

  // bench-HOP-MARK-DELIVERED-CASH.CB-A — Mark delivered's cash prompt (delivery orders only).
  markDelivered: 'Mark delivered',
  markDeliveredTitle: 'Cash collected on delivery',
  markDeliveredBody: 'Enter the cash Bilal was handed for this order, then complete it.',
  markDeliveredAmountLabel: 'Cash amount (USD)',
  markDeliveredConfirm: 'Record & mark delivered',
  markDeliveredDismiss: 'Cancel',
  markDeliveredAmountInvalid: 'Enter a valid amount (0 or more).',
  markDeliveredCashFailed: "Couldn't record the cash — order not marked delivered. Try again.",

  // bench-INTAKE-NEW-ORDER — new strings only, same dictionary/pattern as above.
  newOrderTitle: 'New order',
  newOrderEntityId: 'Entity ID',
  newOrderCustomerId: 'Customer ID',
  newOrderItemsTitle: 'Items',
  newOrderProductId: 'Product ID',
  newOrderQuantity: 'Quantity',
  newOrderVariantId: 'Variant ID (optional)',
  newOrderAddItem: 'Add item',
  newOrderRemoveItem: 'Remove',
  newOrderNotes: 'Notes',
  newOrderChannel: 'Channel',
  newOrderChannelPhone: 'Phone',
  newOrderChannelWhatsapp: 'WhatsApp',
  newOrderChannelCounter: 'Counter',
  newOrderChannelShop: 'Shop',
  newOrderChannelToters: 'Toters',
  newOrderChannelRequired: "Choose the order's channel.",
  newOrderSourceSystem: 'Source system',
  newOrderSourceOrderId: 'Source order ID',
  newOrderOrderType: 'Order type',
  newOrderCurrency: 'Currency',
  newOrderFxRateId: 'FX rate ID',
  newOrderFulfillmentMethod: 'Fulfillment method',
  newOrderDeliveryZoneId: 'Delivery zone ID',
  newOrderDeliveryLocationId: 'Delivery location ID',
  newOrderSelectPlaceholder: '—',
  newOrderSubmit: 'Create order',
  newOrderSubmitting: 'Creating…',
  newOrderEntityIdRequired: 'Entity ID is required.',
  newOrderCustomerIdRequired: 'Customer ID is required.',
  newOrderItemsMin: 'Add at least one item.',
  newOrderItemProductRequired: 'Product ID is required for this item.',
  newOrderItemQuantityInvalid: 'Quantity must be between 1 and 1000.',
  newOrderSubmitErrorGeneric: 'Could not create order. Check your connection and try again.',

  // bench-DELIVERY-FEE-CHANGE.CB-F — change a delivery fee before delivery.
  changeDeliveryFee: 'Change delivery fee',
  feeChangeTitle: 'Change delivery fee',
  feeChangeBody: 'Record a corrected fee before this order is delivered — the total moves with it.',
  feeChangeAmountLabel: 'New fee (USD)',
  feeChangeReasonLabel: 'Reason',
  feeChangeReasonCombined: 'Combined with another order',
  feeChangeReasonOther: 'Other',
  feeChangeLinkedOrderCodeLabel: 'Other order code',
  feeChangeLinkedOrderCodePlaceholder: 'e.g. TKF-AB12CD',
  feeChangeNoteLabel: 'Note',
  feeChangeSave: 'Save',
  feeChangeSaving: 'Saving…',
  feeChangeCancel: 'Cancel',
  feeChangeDone: 'Done',
  feeChangeSuccess: 'Delivery fee updated.',
  feeChangeStillCharges: (code: string, fee: string) => `Order ${code} still charges ${fee} delivery`,
  feeChangeErrorNoOrder: 'No order has that code.',
  feeChangeErrorMultipleOrders: 'More than one order has that code — ask for the full order.',
  feeChangeErrorOtherCustomer: 'That order belongs to another customer.',
  feeChangeErrorNoChange: 'That is already this order’s delivery fee.',
  feeChangeErrorPickup: 'A pickup order has no delivery fee.',
  feeChangeErrorWrongStatus: 'This order’s status no longer allows a delivery fee change.',
  feeChangeErrorGeneric: 'Could not save the delivery fee. Try again.',
  // R562: a delivered order whose invoice is still held.
  feeChangePostDeliveryBody: 'This corrects a delivered order\'s fee before its invoice posts. The total moves with it.',
  feeChangeErrorInvoicePosted: 'This order\'s invoice has posted. Its fee can no longer be changed here.',
  feeChangeErrorInvoiceFailed: 'This order\'s invoice post failed. It needs fixing before the fee can change.',
  feeChangeErrorNoHeldInvoice: 'This delivered order has no held invoice to correct.',
  // R142 (B) — new-order pickers (Rook step 0; the lane never edits this file)
  newOrderCustomerPhone: 'Customer phone',
  newOrderFind: 'Find',
  newOrderCustomerPick: 'Pick the customer',
  newOrderCustomerNone: 'No customer with this number. Add a name if you have one, then create.',
  newOrderCustomerName: 'Name (optional)',
  newOrderCreateCustomer: 'Create customer',
  newOrderCustomerNoName: '(no name)',
  newOrderCustomerChange: 'Change',
  newOrderCustomerRequired: 'Find or create the customer first.',
  newOrderPhoneInvalid: "That number doesn't look right. Check it and try again.",
  newOrderLookupForbidden: "This login can't look up customers. Use a hop or owner login.",
  newOrderLookupFailed: "Couldn't reach the server. Check the connection and try again.",
  newOrderProductSearch: 'Search products',
  newOrderSearch: 'Search',
  newOrderProductNone: 'No products match.',
  newOrderProductRequired: 'Pick a product.',
  newOrderVariant: 'Variant',
  newOrderVariantDefault: 'Default',
  newOrderZone: 'Delivery zone',
  newOrderLocation: 'Delivery area',
  newOrderZoneRequired: 'Pick a delivery zone and area.',
  newOrderNameTooShort: 'The name needs at least 2 letters.',
  newOrderFulfillmentRequired: 'Choose pickup or delivery.',

  // R487 R488 (Dee via Rook #66417, Rook #66451): product search as you type, the running total.
  newOrderProductSearching: 'Searching\u2026',
  newOrderProductMinChars: 'Type 2 or more letters to search.',
  newOrderSearchOffline: 'Offline: searching needs a connection.',
  newOrderNoLines: 'No items yet. Search above and tap a product to add it.',
  newOrderTotalTitle: 'Order total',
  newOrderLinesSubtotal: 'Items',
  newOrderDeliveryFee: 'Delivery fee',
  newOrderDeliveryFeePending: 'Pick a delivery area to add its fee.',
  newOrderTotal: 'Total',
  newOrderTotalNote: 'Any promotion is applied when the order is created.',
  // Rook #66451: a product with more than one variant needs a real choice.
  newOrderVariantRequired: 'Pick a variant for this item.',

  // R485 client half (Dee via Rook #66417, #66468): the phone box lists customers as you type.
  newOrderPhoneReadsAs: 'Reads as',
  newOrderCustomerSearching: 'Looking up\u2026',
  // {count} is filled by the screen.
  newOrderCustomerMore: '{count} more, keep typing',
  newOrderCustomerRateLimited: 'Too many searches, wait a moment.',

  // R264 (b): the AR values of these three keys are English stubs; Rook's commit on the lane
  // branch puts the Arabic in (R197 (b) precedent). {slots} are filled by board-i18n's fillSlots.
  actionFailed: 'Order {code}: {message}',
  kitchenOpen: 'Order {code}: {count} meal item(s) not finished in the kitchen',
  moveAnyway: 'Move anyway',

  // R459: Edit items (HOP order page). Strings only; the dialog is EditItemsDialog.tsx.
  editItemsTitle: 'Edit items',
  editItemsBody: 'Add, remove or change items before the order is ready for dispatch. The total and the invoice follow.',
  editItemsDecrease: 'Decrease quantity',
  editItemsIncrease: 'Increase quantity',
  editItemsRemove: 'Remove',
  editItemsMinOne: 'Lowest quantity is 1. Use Remove to take the item off.',
  editItemsLastLine: 'An order needs at least one item. To drop the whole order, cancel it.',
  editItemsAddLabel: 'Add a product',
  editItemsSearch: 'Search',
  editItemsSearching: 'Searching\u2026',
  editItemsNoProducts: 'No products found.',
  editItemsSearchFailed: "Couldn't search products. Try again.",
  editItemsReasonLabel: 'Reason (optional)',
  editItemsSave: 'Save items',
  editItemsSaving: 'Saving\u2026',
  editItemsCancel: 'Cancel',
  editItemsNoChange: 'Nothing has changed yet.',
  editItemsOffline: 'Offline: editing items needs a connection.',
  editItemsCutoff: 'This order is ready for dispatch or further along; its items can no longer be edited.',
  editItemsMoved: 'This order changed while you were editing. Close and check it again.',
  editItemsFailed: (message: string) => `Could not save the items: ${message}`,
}

const AR: typeof EN = {
  back: 'رجوع',
  loading: 'جارٍ تحميل الطلب…',
  errorLoading: 'تعذّر تحميل هذا الطلب.',
  retry: 'إعادة المحاولة',
  notFound: 'الطلب غير موجود.',

  statusUpdated: 'تم تحديث الحالة',
  undo: 'تراجع',
  undoingIn: (s: number) => `تراجع (${s}ث)`,
  advanceConfirmNone: 'لا خطوة تالية من هذه الحالة.',

  actions: 'الإجراءات',
  cancelOrder: 'إلغاء الطلب',
  cancelConfirmTitle: 'إلغاء هذا الطلب؟',
  cancelConfirmBody: 'لا يمكن التراجع عن هذا الإجراء. سيتم إشعار الزبون.',
  cancelConfirmAction: 'نعم، ألغِ الطلب',
  cancelConfirmDismiss: 'الاحتفاظ بالطلب',
  amend: 'تعديل',
  trackLink: 'رابط تتبع المشغّل',

  channel: 'القناة',
  sourceSystem: 'المصدر',
  fulfillmentMethod: 'التنفيذ',
  orderType: 'النوع',
  delivery: 'توصيل',
  pickup: 'استلام',
  placedAt: 'تاريخ الطلب',

  customer: 'الزبون',
  whatsapp: 'واتساب',
  language: 'اللغة',
  returning: 'زبون عائد',
  newCustomer: 'زبون جديد',
  ordersCount: 'طلبات',
  spent: 'منفق',
  testOrder: 'تجريبي',
  whatsappMessage: (ref: string) =>
    `Hi! Following up on your order ${ref}. / مرحباً! بخصوص طلبك ${ref}.`,

  deliveryTitle: 'التوصيل',
  street: 'الشارع',
  building: 'المبنى / الطابق',
  city: 'المدينة',
  zone: 'المنطقة',
  deliveryFee: 'رسوم التوصيل',
  originalFee: 'الأصلي',
  deliveryDate: 'التاريخ',
  deliverySlot: 'الفترة',
  deliveryNotes: 'معلم / اتجاهات',
  giftMessage: 'رسالة الهدية',
  noDeliveryInfo: 'طلب استلام — لا يوجد عنوان توصيل.',

  itemsTitle: 'ما يجب تحضيره',
  qty: 'الكمية',
  each: 'للقطعة',
  customizations: 'تخصيصات',
  allergenFlag: 'تحقّق من المسبّبات/البدائل',
  promoTag: 'عرض',
  noItems: 'لا توجد عناصر في هذا الطلب.',
  unmappedItem: 'ليس على قائمتنا: تحقّق من هذا الصنف مع توترز',

  totalsTitle: 'الإجمالي',
  subtotal: 'المجموع الفرعي',
  grandTotal: 'الإجمالي الكلي',
  cod: 'الدفع عند الاستلام',

  activityTitle: 'النشاط',
  noActivity: 'لا توجد تغييرات حالة بعد.',
  changedBySystem: 'النظام',
  from: 'من',
  to: 'إلى',

  roleView: 'عرض فقط',

  // bench-HOP-MARK-DELIVERED-CASH.CB-A — نافذة تأكيد "تم التسليم" (لطلبات التوصيل فقط).
  markDelivered: 'تم التسليم',
  markDeliveredTitle: 'المبلغ النقدي المُحصَّل عند التسليم',
  markDeliveredBody: 'أدخل المبلغ النقدي الذي استلمه بلال لهذا الطلب، ثم أكمل الطلب.',
  markDeliveredAmountLabel: 'المبلغ النقدي (دولار أمريكي)',
  markDeliveredConfirm: 'تسجيل وتعليم كمُسلَّم',
  markDeliveredDismiss: 'إلغاء',
  markDeliveredAmountInvalid: 'أدخل مبلغاً صحيحاً (0 أو أكثر).',
  markDeliveredCashFailed: 'تعذّر تسجيل المبلغ النقدي — لم يُعلَّم الطلب كمُسلَّم. حاول مجدداً.',

  // bench-INTAKE-NEW-ORDER — new strings only.
  newOrderTitle: 'طلب جديد',
  newOrderEntityId: 'معرّف الجهة',
  newOrderCustomerId: 'معرّف الزبون',
  newOrderItemsTitle: 'العناصر',
  newOrderProductId: 'معرّف المنتج',
  newOrderQuantity: 'الكمية',
  newOrderVariantId: 'معرّف النوع (اختياري)',
  newOrderAddItem: 'إضافة عنصر',
  newOrderRemoveItem: 'إزالة',
  newOrderNotes: 'ملاحظات',
  newOrderChannel: 'القناة',
  newOrderChannelPhone: 'هاتف',
  newOrderChannelWhatsapp: 'واتساب',
  newOrderChannelCounter: 'زبون كاونتر',
  newOrderChannelShop: 'بيع بالجملة',
  newOrderChannelToters: 'توترز',
  newOrderChannelRequired: 'اختر قناة الطلب.',
  newOrderSourceSystem: 'نظام المصدر',
  newOrderSourceOrderId: 'معرّف طلب المصدر',
  newOrderOrderType: 'نوع الطلب',
  newOrderCurrency: 'العملة',
  newOrderFxRateId: 'معرّف سعر الصرف',
  newOrderFulfillmentMethod: 'طريقة التنفيذ',
  newOrderDeliveryZoneId: 'معرّف منطقة التوصيل',
  newOrderDeliveryLocationId: 'معرّف موقع التوصيل',
  newOrderSelectPlaceholder: '—',
  newOrderSubmit: 'إنشاء الطلب',
  newOrderSubmitting: 'جارٍ الإنشاء…',
  newOrderEntityIdRequired: 'معرّف الجهة مطلوب.',
  newOrderCustomerIdRequired: 'معرّف الزبون مطلوب.',
  newOrderItemsMin: 'أضف عنصراً واحداً على الأقل.',
  newOrderItemProductRequired: 'معرّف المنتج مطلوب لهذا العنصر.',
  newOrderItemQuantityInvalid: 'يجب أن تكون الكمية بين 1 و1000.',
  newOrderSubmitErrorGeneric: 'تعذّر إنشاء الطلب. تحقق من الاتصال وحاول مجدداً.',

  // bench-DELIVERY-FEE-CHANGE.CB-F — تغيير رسوم التوصيل قبل التسليم.
  changeDeliveryFee: 'تغيير رسوم التوصيل',
  feeChangeTitle: 'تغيير رسوم التوصيل',
  feeChangeBody: 'سجّل رسوماً مصححة قبل تسليم هذا الطلب — يتغيّر الإجمالي معها.',
  feeChangeAmountLabel: 'الرسوم الجديدة (دولار أمريكي)',
  feeChangeReasonLabel: 'السبب',
  feeChangeReasonCombined: 'دُمج مع طلب آخر',
  feeChangeReasonOther: 'سبب آخر',
  feeChangeLinkedOrderCodeLabel: 'رمز الطلب الآخر',
  feeChangeLinkedOrderCodePlaceholder: 'مثال: TKF-AB12CD',
  feeChangeNoteLabel: 'ملاحظة',
  feeChangeSave: 'حفظ',
  feeChangeSaving: 'جارٍ الحفظ…',
  feeChangeCancel: 'إلغاء',
  feeChangeDone: 'تم',
  feeChangeSuccess: 'تم تحديث رسوم التوصيل.',
  feeChangeStillCharges: (code: string, fee: string) => `الطلب ${code} ما زال يتحمّل رسوم توصيل ${fee}`,
  feeChangeErrorNoOrder: 'لا يوجد طلب بهذا الرمز.',
  feeChangeErrorMultipleOrders: 'أكثر من طلب يحمل هذا الرمز — اطلب الرقم الكامل للطلب.',
  feeChangeErrorOtherCustomer: 'هذا الطلب يخص زبوناً آخر.',
  feeChangeErrorNoChange: 'هذه هي رسوم التوصيل الحالية لهذا الطلب بالفعل.',
  feeChangeErrorPickup: 'طلب الاستلام لا يحمل رسوم توصيل.',
  feeChangeErrorWrongStatus: 'حالة هذا الطلب لم تعد تسمح بتغيير رسوم التوصيل.',
  feeChangeErrorGeneric: 'تعذّر حفظ رسوم التوصيل. حاول مجدداً.',
  // R562: Rook's Arabic, vault b945d71e8 (pins Rook #68473).
  feeChangePostDeliveryBody: 'هذا يصحّح رسم التوصيل لطلب تم تسليمه قبل أن تُرحَّل فاتورته. يتغيّر المجموع معه.',
  feeChangeErrorInvoicePosted: 'رُحِّلت فاتورة هذا الطلب. لم يعد بالإمكان تغيير رسمه من هنا.',
  feeChangeErrorInvoiceFailed: 'فشل ترحيل فاتورة هذا الطلب. يجب إصلاحه قبل تغيير الرسم.',
  feeChangeErrorNoHeldInvoice: 'لا توجد فاتورة محجوزة لهذا الطلب المسلَّم لتصحيحها.',
  // R142 (B) — Rook's Arabic, never copied by the DC
  newOrderCustomerPhone: 'هاتف الزبون',
  newOrderFind: 'ابحث',
  newOrderCustomerPick: 'اختر الزبون',
  newOrderCustomerNone: 'لا يوجد زبون بهذا الرقم. أضف الاسم إن توفّر، ثم أنشئ.',
  newOrderCustomerName: 'الاسم (اختياري)',
  newOrderCreateCustomer: 'إنشاء زبون',
  newOrderCustomerNoName: '(بلا اسم)',
  newOrderCustomerChange: 'تغيير',
  newOrderCustomerRequired: 'ابحث عن الزبون أو أنشئه أولاً.',
  newOrderPhoneInvalid: 'هذا الرقم لا يبدو صحيحاً. تحقّق منه وحاول مجدداً.',
  newOrderLookupForbidden: 'لا يمكن لهذا الحساب البحث عن الزبائن. استخدم حساب التنسيق أو حساب المالك.',
  newOrderLookupFailed: 'تعذّر الوصول إلى الخادم. تحقّق من الاتصال وحاول مجدداً.',
  newOrderProductSearch: 'ابحث عن المنتجات',
  newOrderSearch: 'بحث',
  newOrderProductNone: 'لا توجد منتجات مطابقة.',
  newOrderProductRequired: 'اختر منتجاً.',
  newOrderVariant: 'الصنف',
  newOrderVariantDefault: 'الافتراضي',
  newOrderZone: 'منطقة التوصيل',
  newOrderLocation: 'حيّ التوصيل',
  newOrderZoneRequired: 'اختر منطقة التوصيل والحيّ.',
  newOrderNameTooShort: 'الاسم يحتاج إلى حرفين على الأقل.',
  newOrderFulfillmentRequired: 'اختر الاستلام أو التوصيل.',

  // R487 R488: Arabic copied byte for byte from Rook's vault table (ecb0ef5e) by the pinned fill script.
  newOrderProductSearching: 'جارٍ البحث عن المنتج…',
  newOrderProductMinChars: 'اكتب حرفين أو أكثر للبحث.',
  newOrderSearchOffline: 'لا يوجد اتصال: البحث يحتاج إلى اتصال بالإنترنت.',
  newOrderNoLines: 'لا توجد أصناف بعد. ابحث في الأعلى واضغط على المنتج لإضافته.',
  newOrderTotalTitle: 'إجمالي الطلب',
  newOrderLinesSubtotal: 'الأصناف',
  newOrderDeliveryFee: 'رسوم التوصيل',
  newOrderDeliveryFeePending: 'اختر منطقة التوصيل لإضافة رسومها.',
  newOrderTotal: 'الإجمالي',
  newOrderTotalNote: 'يُطبَّق أي عرض ترويجي عند إنشاء الطلب.',
  newOrderVariantRequired: 'اختر نوع هذا الصنف.',

  // R485 client half: copied byte for byte by the pinned fill script, the first two from Rook's
  // vault table 1e51ffcb, the last two from 4d187337d.
  newOrderPhoneReadsAs: 'يُقرأ كـ',
  newOrderCustomerSearching: 'جارٍ البحث عن الزبون…',
  newOrderCustomerMore: '{count} آخرون، تابع الكتابة',
  newOrderCustomerRateLimited: 'عمليات بحث كثيرة، انتظر لحظة.',

  // R264 (b): Arabic by Rook's commit on the lane (R197 (b) precedent); {slots} as in the EN block.
  actionFailed: 'الطلب {code}: {message}',
  kitchenOpen: 'الطلب {code}: {count} وجبة لم تكتمل في المطبخ',
  moveAnyway: 'انقله على أي حال',

  // R459: Arabic copy from Rook's table (edit-items-ar-strings.md).
  editItemsTitle: 'تعديل الأصناف',
  editItemsBody: 'أضف أو احذف أو عدّل الأصناف قبل أن يصبح الطلب جاهزًا للتوصيل. المجموع والفاتورة يتحدّثان تلقائيًا.',
  editItemsDecrease: 'تقليل الكمية',
  editItemsIncrease: 'زيادة الكمية',
  editItemsRemove: 'حذف',
  editItemsMinOne: 'أقل كمية هي 1. استخدم "حذف" لإزالة الصنف.',
  editItemsLastLine: 'الطلب يحتاج إلى صنف واحد على الأقل. لإسقاط الطلب كله، ألغِ الطلب.',
  editItemsAddLabel: 'إضافة منتج',
  editItemsSearch: 'بحث',
  editItemsSearching: 'جارٍ البحث...',
  editItemsNoProducts: 'لم يتم العثور على منتجات.',
  editItemsSearchFailed: 'تعذّر البحث عن المنتجات. حاول مرة أخرى.',
  editItemsReasonLabel: 'السبب (اختياري)',
  editItemsSave: 'حفظ الأصناف',
  editItemsSaving: 'جارٍ الحفظ...',
  editItemsCancel: 'إلغاء',
  editItemsNoChange: 'لم يتغيّر شيء بعد.',
  editItemsOffline: 'لا يوجد اتصال: تعديل الأصناف يحتاج إلى اتصال بالإنترنت.',
  editItemsCutoff: 'هذا الطلب جاهز للتوصيل أو تجاوز ذلك؛ لم يعد بالإمكان تعديل أصنافه.',
  editItemsMoved: 'تغيّر هذا الطلب أثناء التعديل. أغلق النافذة وراجعه مجددًا.',
  editItemsFailed: (message: string) => `تعذّر حفظ الأصناف: ${message}`,
}

const ORDER_DICT = { en: EN, ar: AR } as const

export function ot<K extends keyof typeof EN>(lang: Lang, key: K): (typeof EN)[K] {
  return ORDER_DICT[lang][key]
}

// Human-readable, bilingual labels for the one-tap forward-advance button — the verb for
// the action that MOVES TO this status (keyed by the TARGET of FORWARD_TRANSITIONS, not
// the current status), so branching FROM states (CONFIRMED -> IN_KITCHEN or
// READY_FOR_DISPATCH) get distinct verbs per target. Passive state names live below in
// STATUS_BADGE_LABEL. PENDING/CANCELLED/REFUNDED are never a forward-advance target —
// present only to satisfy the Record<OrderStatusValue, ...> shape.
export const STATUS_ADVANCE_LABEL: Record<OrderStatusValue, { en: string; ar: string }> = {
  PENDING: { en: 'Pending', ar: 'قيد الانتظار' },
  CONFIRMED: { en: 'Confirm order', ar: 'تأكيد الطلب' },
  IN_KITCHEN: { en: 'Start kitchen prep', ar: 'بدء التحضير' },
  AWAITING_RETAIL_PACK: { en: 'Move to retail pack', ar: 'نقل للتعبئة بالتجزئة' },
  // R78 — Rook's line (Chuck #41595: Arabic is Rook's commit, never copied by the DC).
  AWAITING_FINAL_CHECK: { en: 'Send to final check', ar: 'إرسال للمراجعة النهائية' },
  READY_FOR_DISPATCH: { en: 'Mark ready for dispatch', ar: 'جاهز للإرسال' },
  OUT_FOR_DELIVERY: { en: 'Send out for delivery', ar: 'إرسال للتوصيل' },
  DELIVERED: { en: 'Mark delivered', ar: 'تم التسليم' },
  CANCELLED: { en: 'Cancelled', ar: 'ملغي' },
  REFUNDED: { en: 'Refunded', ar: 'مسترجع' },
}

// Passive state-name badge labels.
// bench-HOP-CAN-FINAL-CHECK.CB-F: Bilal's final-check action on HOP's order screen — a
// distinct action, not a forward-advance target, so it is its own bilingual pair rather
// than a STATUS_ADVANCE_LABEL entry. Arabic is Rook's commit, byte-for-byte (Chuck #41595
// convention); copied here verbatim from the bench text, never typed by the lane.
export const FINAL_CHECK_ACTION_LABEL = { en: 'Final check done', ar: 'تمت المراجعة النهائية' }

export const STATUS_BADGE_LABEL: Record<OrderStatusValue, { en: string; ar: string }> = {
  PENDING: { en: 'Pending', ar: 'قيد الانتظار' },
  CONFIRMED: { en: 'Confirmed', ar: 'مؤكّد' },
  IN_KITCHEN: { en: 'In kitchen', ar: 'قيد التحضير' },
  AWAITING_RETAIL_PACK: { en: 'Awaiting retail pack', ar: 'بانتظار التعبئة' },
  // R78 — Rook's line (Chuck #41595: Arabic is Rook's commit, never copied by the DC).
  AWAITING_FINAL_CHECK: { en: 'Awaiting final check', ar: 'بانتظار المراجعة النهائية' },
  READY_FOR_DISPATCH: { en: 'Ready for dispatch', ar: 'جاهز للإرسال' },
  OUT_FOR_DELIVERY: { en: 'Out for delivery', ar: 'قيد التوصيل' },
  DELIVERED: { en: 'Delivered', ar: 'تم التسليم' },
  CANCELLED: { en: 'Cancelled', ar: 'ملغي' },
  REFUNDED: { en: 'Refunded', ar: 'مسترجع' },
}
